import { faCrown } from "@fortawesome/free-solid-svg-icons/faCrown";
import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons/faUserPlus";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Button } from "../../../../components/Button";
import { MemberAvatar } from "../../../../components/MemberAvatar";
import { FontSize } from "../../../../components/Text";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { thresholdSliceSelectors } from "../../../../redux/threshold/threshold.selector";
import { thresholdActions } from "../../../../redux/threshold/threshold.slice";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { TestIDs } from "../../../../tests/types/testIds";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { MemberInviteModal } from "../../../permissions/components/modals/MemberInviteModal";
import { useDrawersContext } from "../../../tasks/drawers-context";
import {
  SetupCard,
  SetupCardActions,
  SetupCardDescription,
  SetupCardTitle,
} from "./DashboardSetupCard";
import { DashboardSetupCardLabel } from "./DashboardSetupCardLabel";
import { DashboardSetupPlanBadge } from "./DashboardSetupPlanBadge";

const Avatars = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
`;

const EmptySeat = styled.span`
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: ${Rounded.circle};
  border: 1.5px dashed ${({ theme }) => theme.darkerSub};
  color: ${({ theme }) => theme.darkerSub};
`;

const ManageLink = styled(Link)`
  font-size: ${FontSize.s};
  color: ${({ theme }) => theme.main};

  &:hover {
    color: ${({ theme }) => theme.main};
    text-decoration: underline;
  }
`;

type DashboardSetupTeamCardProps = {
  featured?: boolean;
  done?: boolean;
};

export const DashboardSetupTeamCard: React.FC<DashboardSetupTeamCardProps> = ({
  featured,
  done,
}) => {
  const [inviteOpen, setInviteOpen] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setSubToUnlock } = useDrawersContext();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const thresholds = useAppSelector(
    thresholdSliceSelectors.selectThresholdStats,
  );

  const thresholdsLoading = useAppSelector(
    thresholdSliceSelectors.selectThresholdLoading,
  );

  const outOfSeats =
    Boolean(thresholds) &&
    (thresholds?.consumed?.USERS || 0) >= (thresholds?.total?.USERS || 0);

  const onInvite = () => {
    if (outOfSeats) {
      setSubToUnlock({
        visible: true,
        title: t("subToUnlock.specific.seatsCount"),
      });
      return;
    }

    setInviteOpen(true);
  };

  useEffect(() => {
    dispatch(thresholdActions.getThresholds({ userId }));
  }, [dispatch, userId]);

  return (
    <SetupCard
      $featured={featured}
      $done={done}
      data-testid={TestIDs.dashboard.setup.card("TEAM")}
    >
      <DashboardSetupCardLabel featured={featured} done={done} />
      {outOfSeats ? <DashboardSetupPlanBadge /> : null}
      <SetupCardTitle $featured={featured} $done={done}>
        {t("dashboard.setup.team.title")}
      </SetupCardTitle>
      <SetupCardDescription $done={done}>
        {t("dashboard.setup.team.description")}
      </SetupCardDescription>

      <Avatars aria-hidden>
        <MemberAvatar member={user} size={36} />
        {[0, 1, 2].map((seat) => (
          <EmptySeat key={seat}>
            <AtiraIcon icon={outOfSeats ? faLock : faPlus} />
          </EmptySeat>
        ))}
      </Avatars>

      <SetupCardActions>
        <Button
          icon={outOfSeats ? faCrown : faUserPlus}
          onClick={onInvite}
          loading={!thresholds && thresholdsLoading}
          dataTestId={TestIDs.dashboard.setup.cta("TEAM")}
        >
          {outOfSeats
            ? t("dashboard.setup.see_plans")
            : t("dashboard.setup.team.cta")}
        </Button>
        <ManageLink to="/permissions">
          {t("dashboard.setup.team.manage")}
        </ManageLink>
      </SetupCardActions>

      <MemberInviteModal
        isOpen={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
    </SetupCard>
  );
};
